import './MembershipTypeHeader.css';
import type { MembershipTypeRow } from '../api/membership.api';
import { resolveThemeToken } from '../themeTokens';

type DetailHeaderProps = {
  variant?: 'detail';
  membershipType?: MembershipTypeRow;
  activeTab: 'overview' | 'members' | 'settings';
  onTabChange: (tab: 'overview' | 'members' | 'settings') => void;
  onBack?: () => void;
};

type OverviewHeaderProps = {
  variant: 'overview';
  activeTab: 'tiers' | 'members' | 'actions';
  onTabChange: (tab: 'tiers' | 'members' | 'actions') => void;
  onPrimaryAction?: () => void;
  tabCounts?: { tiers?: number; members?: number; actions?: number };
};

type MembershipTypeHeaderProps = DetailHeaderProps | OverviewHeaderProps;

const formatRupeesFromPaise = (value: number): string => `₹${(Math.max(value || 0, 0) / 100).toLocaleString('en-IN')}`;

const MembershipTypeHeader = (props: MembershipTypeHeaderProps) => {
  if (props.variant === 'overview') {
    return (
      <section className="membership-type-header">
        <div className="membership-type-header__top">
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.3px', margin: 0, color: '#f1f5f9' }}>Memberships</h2>
            <p className="membership-type-header__subtitle">
              Manage your loyalty tiers, members, and engagement
            </p>
          </div>
          {props.onPrimaryAction && (
            <button type="button" className="btn-cta" onClick={props.onPrimaryAction} style={{ padding: '9px 16px', fontSize: 12, fontWeight: 700, borderRadius: 10, cursor: 'pointer', background: 'var(--color-accent)', color: '#FFFFFF', border: 'none', boxShadow: '0 0 20px rgba(243,119,77,0.18)' }}>
              + New tier
            </button>
          )}
        </div>

        <div className="membership-type-header__tabs">
          {(['tiers', 'members', 'actions'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              className={`membership-type-header__tab ${props.activeTab === tab ? 'is-active' : ''}`}
              onClick={() => props.onTabChange(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {props.tabCounts?.[tab] !== undefined ? (
                <span className="membership-tab__badge">{props.tabCounts[tab]}</span>
              ) : null}
            </button>
          ))}
        </div>
      </section>
    );
  }

  const { membershipType, activeTab, onTabChange, onBack } = props;
  const theme = membershipType?.themeColorToken ? resolveThemeToken(membershipType.themeColorToken) : null;

  return (
    <section className="membership-type-header membership-type-header--detail">
      {theme ? (
        <div
          className="membership-type-header__hero-banner"
          style={{ background: theme.cardBackground }}
        />
      ) : null}
      <div className="membership-type-header__top">
        <div className="dashboard-tab-hero-copy">
          <p className="dashboard-tab-hero-eyebrow">Membership Type</p>
          <h3 className="dashboard-tab-hero-title">
            <span
              className={`membership-type-header__status-dot ${membershipType?.isActive ? 'is-active' : 'is-inactive'}`}
            />
            {membershipType?.name || 'Membership type'}
          </h3>
          <p className="membership-type-header__subtitle">{membershipType?.description || 'Manage this type settings, members, and health.'}</p>
          <div className="membership-type-header__meta">
            <span className="membership-type-header__pill is-accent">Tier {membershipType?.tierLevel || 1}</span>
            <span className="membership-type-header__pill">{membershipType?.pricePaise === 0 ? 'Free tier' : formatRupeesFromPaise(membershipType?.pricePaise || 0)}</span>
            <span className="membership-type-header__pill">{membershipType?.validityDays || 0} day validity</span>
            <span className="membership-type-header__pill">{membershipType?.pointsEnabled ? 'Points enabled' : 'Points off'}</span>
          </div>
        </div>
        <button type="button" className="btn ghost" onClick={onBack}>Back</button>
      </div>

      <div className="membership-type-header__tabs">
        {(['overview', 'members', 'settings'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            className={`membership-type-header__tab ${activeTab === tab ? 'is-active' : ''}`}
            onClick={() => onTabChange(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
    </section>
  );
};

export default MembershipTypeHeader;
